import { useState } from "react";

export interface FilterOption {
  value: string;
  count: number;
}

interface Props {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  max?: number;
  /** Translates a raw option value into a human label (e.g. SlownessBucket → "On Track"). */
  formatValue?: (value: string) => string;
  /** Render a find-as-you-type input above the options. Useful for long lists
   *  (Assigned to, Client) where scrolling past "Show N more" is tedious. */
  searchable?: boolean;
  /** Synthetic refinement checkboxes rendered below the option list, inside the same card.
   *  Used by the Request Type filter to host the "Onboarding" (≥7) and "Redeployment" (≤6)
   *  buckets — derived options that live visually alongside the request-type checkboxes. */
  extraOptions?: {
    label: string;
    checked: boolean;
    onToggle: () => void;
  }[];
}

export function FilterGroup({
  label,
  options,
  selected,
  onChange,
  max = 6,
  formatValue,
  extraOptions,
  searchable,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const byLabel = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: "base" });
  const labelOf = (value: string) => (formatValue ? formatValue(value) : value);

  // Sort raw options alphabetically by their displayed label (not by count), then take the
  // collapsed slice. The synthetic extraOptions (e.g. Onboarding / Redeployment) are always
  // visible and merged into the displayed set so the whole list reads alphabetically together.
  const rawSorted = [...options].sort((a, b) =>
    byLabel(labelOf(a.value), labelOf(b.value)),
  );
  // A non-empty search query takes over the list: show every match (no slice, no expand/collapse)
  // since the input replaces the "Show N more" affordance. Empty query behaves exactly as before.
  const q = query.trim().toLowerCase();
  const matches = q
    ? rawSorted.filter((o) => labelOf(o.value).toLowerCase().includes(q))
    : rawSorted;
  const visibleRaw = q ? matches : expanded ? matches : matches.slice(0, max);

  const entries = [
    ...visibleRaw.map((opt) => ({
      key: opt.value,
      label: labelOf(opt.value),
      checked: selected.includes(opt.value),
      onToggle: () =>
        selected.includes(opt.value)
          ? onChange(selected.filter((x) => x !== opt.value))
          : onChange([...selected, opt.value]),
    })),
    ...(extraOptions ?? []).map((e) => ({
      key: `extra:${e.label}`,
      label: e.label,
      checked: e.checked,
      onToggle: e.onToggle,
    })),
  ].sort((a, b) => byLabel(a.label, b.label));

  const activeCount =
    selected.length + (extraOptions?.filter((e) => e.checked).length ?? 0);

  return (
    <div className="filter-group">
      <div className="label">
        <span>{label}</span>
        {activeCount > 0 && <span className="badge">{activeCount}</span>}
      </div>
      {searchable && (
        <input
          type="text"
          className="filter-search"
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      <div className="filter-options short">
        {entries.map((entry) => (
          <label key={entry.key}>
            <input type="checkbox" checked={entry.checked} onChange={entry.onToggle} />
            <span>{entry.label}</span>
          </label>
        ))}
      </div>
      {searchable && q && entries.length === 0 && (
        <div className="filter-empty">No matches</div>
      )}
      {options.length > max && !q && (
        <button
          type="button"
          className="btn btn-text btn-sm"
          style={{ padding: "4px 0", fontSize: 11 }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show less" : `Show ${options.length - max} more`}
        </button>
      )}
    </div>
  );
}
